import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {SequenceNodeOutputData} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {SequenceCardScratchpadState} from '../../scratchpad/sequenceCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const editRegularOutputInputSchema = z.object({
  outputId: z.number().describe('The id of the regular output to edit'),
  name: z.string().optional().nullable().describe('The new name for the output'),
  maxRepeats: z
    .number()
    .int()
    .positive()
    .min(1)
    .optional()
    .nullable()
    .describe('The new frequency cap for the output (minimum 1)'),
});

export default function editRegularOutputTool() {
  async function execute(
    params: ToolExecuteParams<typeof editRegularOutputInputSchema, SequenceCardScratchpadState>,
  ) {
    const {outputId, name, maxRepeats} = params.input;
    const validatedScratchpad = validateAgentScratchpad<SequenceCardScratchpadState>(
      params.toolUtils.agentScratchpad,
      NodeType.SEQUENCE,
    );
    const {card} = validatedScratchpad;

    const existingOutput = card.outputs.find((output) => output.id === outputId) as
      | SequenceNodeOutputData
      | undefined;

    if (!existingOutput) {
      return toolError(`Output with id ${outputId} not found.`);
    }

    if (existingOutput.type === OutputType.Fallback) {
      return toolError(
        'The Fallback output is a built-in system output for unidentified users and cannot be edited.',
      );
    }

    if (existingOutput.type === OutputType.Last) {
      return toolError(
        'The Last output cannot be edited directly. When the repeat mode is "Infinite" there is no Last output; when the mode is "Finite" there is a Last output, and it is managed by the system.',
      );
    }

    const changes: string[] = [];

    if (name !== undefined && name !== null) {
      existingOutput.name = name;
      changes.push(`name to "${name}"`);
    }

    if (maxRepeats !== undefined && maxRepeats !== null) {
      existingOutput.maxRepeats = maxRepeats;
      changes.push(`frequency cap to ${maxRepeats}`);
    }

    if (changes.length === 0) {
      return toolError('No changes specified for regular output');
    }

    return toolResult(`Regular output updated: ${changes.join(', ')}`);
  }

  return tool<typeof editRegularOutputInputSchema, SequenceCardScratchpadState>({
    name: V2CardAgentToolName.EditRegularOutput,
    displayName: 'Editing regular output',
    description:
      'Edit an existing regular output name and/or frequency cap (maxRepeats). A regular output is a step in the sequence that determines how many times a user is exposed to the content connected to it.',
    inputSchema: editRegularOutputInputSchema,
    execute,
  });
}
